import type { Account } from "@tandemdiabetes/sugarmate-api-react-client"
import { GlucoseUnit } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Legend,
  InfoButton,
  FieldSet,
  SelectButtons,
  SelectButton,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEvent } from "react"
import { useCallback } from "react"

import styles from "./AccountDetails.module.scss"
import { usePolyglot } from "src/contexts"
import {
  useInputPropsForValue,
  useGlucoseUnit,
  useAccountProperty,
  useUpdateAccount,
  useDebouncedUpdater,
} from "src/hooks"

const polyglotPrefix = "pages.account.account_details.glucose_units."

export function GlucoseUnits() {
  const accountId = useAccountProperty(
    "id",
    NaN,
  )

  const updatedAt = useAccountProperty(
    "updated_at",
    undefined,
  )

  const polyglot = usePolyglot()
  const currentGlucoseUnit = useGlucoseUnit()

  const updateAccount = useUpdateAccount()
  const debouncedUpdateAccount = useDebouncedUpdater({ updater: updateAccount })

  const debouncedUpdateAccountWithId = useCallback(
    (account: Partial<Account>) => debouncedUpdateAccount({
      id: accountId,
      ...account,
    }),
    [
      debouncedUpdateAccount,
      accountId,
    ],
  )

  const adapter = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => e.target.value as GlucoseUnit,
    [],
  )

  const onValueChange = useCallback(
    (glucoseUnit: Account["units"]) => {
      debouncedUpdateAccountWithId({
        units: glucoseUnit,
      })
    },
    [debouncedUpdateAccountWithId],
  )

  const {
    value,
    onChange,
  } = useInputPropsForValue({
    value: currentGlucoseUnit,
    onChange: onValueChange,
    adapter,
    updatedAt,
  })

  return (
    <FieldSet>
      <Legend>
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}label`)}>
          {polyglot.t(`${polyglotPrefix}description`)}
        </InfoButton>
      </Legend>
      <SelectButtons className={styles.selectContainer}>
        <SelectButton
          htmlFor="glucose-unit-mgdl"
          name="glucose-unit"
          value={GlucoseUnit.MilligramsPerDeciliter}
          checked={value === GlucoseUnit.MilligramsPerDeciliter}
          onChange={onChange}
        >
          {polyglot.t(`${polyglotPrefix}options.mgdl`)}
        </SelectButton>
        <SelectButton
          htmlFor="glucose-unit-mmoll"
          name="glucose-unit"
          value={GlucoseUnit.MillimolesPerLiter}
          checked={value === GlucoseUnit.MillimolesPerLiter}
          onChange={onChange}
        >
          {polyglot.t(`${polyglotPrefix}options.mmoll`)}
        </SelectButton>
      </SelectButtons>
    </FieldSet>
  )
}
